import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { ReactNode, ButtonHTMLAttributes } from 'react';
import { currentLanguage } from '@/core/i18n';
import { DIRHAM_SIGN, isDirham } from '@/core/dirham';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/* -----------------------------------------------------------------------------
 * Dropdown menu
 * -----------------------------------------------------------------------------
 * One shell, one item, used by every small menu in the app.
 *
 * These were written inline in three places and drifted into the same bug in
 * all three: the item carried the *same* radius as the menu around it, so its
 * corners fought the container's and every option looked welded to the edge.
 * Shared so the geometry is decided once.
 *
 * The rule: inner radius = outer radius − padding. `rounded-nested` is that
 * subtraction, defined in app.css.
 * -------------------------------------------------------------------------- */
export function Menu({
  children, className, align = 'end',
}: {
  children: ReactNode;
  className?: string;
  /** Which edge the menu hangs from. `end` respects Arabic. */
  align?: 'start' | 'end';
}) {
  return (
    <div
      role="menu"
      className={cn(
        'absolute z-20 mt-2 p-1.5 flex flex-col gap-0.5',
        'bg-surface border border-line rounded-card shadow-pop',
        // `pop-enter` keeps it consistent with the other overlays rather than
        // appearing instantly while everything else animates.
        'pop-enter origin-top',
        align === 'end' ? 'end-0' : 'start-0',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function MenuItem({
  children, active, icon, className, ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  active?: boolean;
  icon?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        'w-full flex items-center gap-2.5 px-2.5 h-9 text-start',
        'rounded-nested text-[13px] press',
        active
          ? 'bg-brand-subtle text-brand font-semibold'
          : 'text-muted hover:bg-surface-2 hover:text-text active:bg-surface-3',
        className,
      )}
      {...rest}
    >
      {icon}
      <span className="truncate">{children}</span>
    </button>
  );
}

/* -----------------------------------------------------------------------------
 * Card
 * -------------------------------------------------------------------------- */
export function Card({
  children, className, padded = true,
}: { children: ReactNode; className?: string; padded?: boolean }) {
  return (
    <div
      className={cn(
        /* The border separates; the shadow is a hint, not a lift. Twenty cards
           on a dashboard used to be twenty soft grey haloes, and shadow
           everywhere is shadow nowhere. See the elevation note in theme.css. */
        'bg-surface border border-line rounded-card shadow-card',
        padded && 'p-4 sm:p-5',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title, subtitle, action,
}: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    /* `flex-wrap` with `min-w-0 flex-1` on the text and `shrink-0` on the
       action. Without the first two, flexbox shrinks the text column below its
       content while the buttons hold their width — which on the salary card
       squeezed a two-line subtitle into a column one word wide, reading
       vertically down the screen. Without `flex-wrap`, the fix would instead
       squeeze the buttons. Below `sm` the action drops to its own line, which
       is the only honest answer when both genuinely need the width. */
    <div className="flex items-start justify-between gap-x-4 gap-y-2 mb-4 flex-wrap">
      <div className="min-w-0 flex-1 basis-[min(100%,16rem)]">
        {/* 600 rather than 700. Plex at semibold already holds its own against
            13.5px body text, and bold at this size reads as shouting on a
            screen that has twenty of them. */}
        <h2 className="text-lead font-semibold tracking-tight leading-snug">{title}</h2>
        {subtitle && (
          <p className="text-small text-muted mt-1 leading-snug">{subtitle}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/* -----------------------------------------------------------------------------
 * Status pill
 *
 * Status colour is separate from the brand accent on purpose: on an attendance
 * grid, red must always mean absent — never "this theme happens to be reddish".
 * -------------------------------------------------------------------------- */
export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'flag' | 'brand';

const toneClasses: Record<Tone, string> = {
  neutral: 'bg-surface-3 text-muted',
  ok:      'bg-ok-subtle text-ok',
  warn:    'bg-warn-subtle text-warn',
  danger:  'bg-danger-subtle text-danger',
  info:    'bg-info-subtle text-info',
  flag:    'bg-flag-subtle text-flag',
  brand:   'bg-brand-subtle text-brand',
};

export function Pill({
  children, tone = 'neutral', className,
}: { children: ReactNode; tone?: Tone; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-pill',
        /* Sentence case at 11px, which is what it was.
           ---------------------------------------------------------------------
           It was briefly uppercase with wide tracking, on the reasoning that a
           status is a category rather than a word. The reasoning was fine and
           the result was not: uppercase adds no descenders and every letter its
           full cap height, so at the *same* nominal size it reads a size
           larger — and the tracking widened it again. A pill sits inside a table
           row beside a name, and one that has grown in both directions stops
           being a marker and starts being a second column. */
        'text-[11px] font-semibold leading-tight whitespace-nowrap',
        toneClasses[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/* -----------------------------------------------------------------------------
 * Button
 * -------------------------------------------------------------------------- */
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';

/*
 * Every variant carries an `active:` state, not just the primary one.
 *
 * `hover:` is not a substitute. A finger has no hover, and on Android a tapped
 * control can be left *stuck* in its hover state afterwards — so the one
 * variant that had a pressed state gave feedback and the other four gave none,
 * which is most of why the phone build felt slower than it is.
 */
const buttonVariants: Record<ButtonVariant, string> = {
  primary:   'bg-brand text-brand-fg hover:bg-brand-hover active:bg-brand-active',
  secondary: 'bg-surface text-text border border-line hover:bg-surface-2 active:bg-surface-3 active:border-line-strong',
  ghost:     'text-muted hover:text-text hover:bg-surface-2 active:bg-surface-3 active:text-text',
  danger:    'bg-danger text-danger-fg hover:opacity-90 active:opacity-80',
  success:   'bg-ok text-ok-fg hover:opacity-90 active:opacity-80',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  icon?: ReactNode;
}

/**
 * Sizes are fixed heights rather than padding, so a button that swaps its
 * label for "Saving…" does not resize and shift everything beside it.
 */

export function Button({
  variant = 'primary', size = 'md', icon, className, children, ...rest
}: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-card font-medium',
        // `press` carries the colour transition as well as the shrink; see
        // app.css for why the two cannot be split across two classes.
        'press disabled:opacity-50 disabled:pointer-events-none',
        // Slightly negative tracking. A button label is three or four words at
        // most and Plex sets them a shade loose at these sizes.
        'tracking-tight whitespace-nowrap',
        // 44px minimum on the large size — a gloved hand on a site in July.
        size === 'sm' && 'text-small px-3 h-8',
        size === 'md' && 'text-body px-4 h-9.5',
        size === 'lg' && 'text-lead px-6 h-12',
        buttonVariants[variant],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

/* -----------------------------------------------------------------------------
 * Stat tile
 * -------------------------------------------------------------------------- */
export function StatTile({
  label, value, sub, tone = 'brand', icon, onClick,
}: {
  label: string;
  value: string | number;
  sub?: string;
  tone?: Tone;
  icon?: ReactNode;
  /** Every figure on this dashboard opens the list behind it. */
  onClick?: () => void;
}) {
  const interactive = Boolean(onClick);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      className={cn(
        'text-start w-full bg-surface border border-line rounded-card p-4',
        'shadow-card press-lg',
        interactive && 'hover:border-line-strong hover:shadow-pop cursor-pointer',
        !interactive && 'cursor-default',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-micro uppercase tracking-caps text-subtle font-semibold">
            {label}
          </p>
          {/* Smaller on a phone. 26px was chosen against "3" and "11"; the
              same tile also carries "AED 20,100", which at that size is wider
              than the 163px a two-up grid gives it and pushed the card out.
              A figure that overflows its own card is worse than a smaller
              figure. */}
          {/* The figure, in the mono face and at a real display size. This is
              the only thing on the tile anybody reads first, and the label
              above it is set at a third of the size for exactly that reason —
              hierarchy by contrast rather than by weight. */}
          <p className="text-[21px] sm:text-hero font-semibold leading-none mt-1.5 tabular wrap-anywhere">
            {value}
          </p>
          {/* Two lines rather than one clipped one. `truncate` on a tile this
              narrow produced "Recorded, …" and "Across the …" — an ellipsis
              where the meaning was, which is worse than no subtitle. */}
          {sub && (
            <p className="text-tiny text-muted mt-1.5 leading-snug line-clamp-2">{sub}</p>
          )}
        </div>
        {icon && (
          /* Hidden on the narrowest screens. Two tiles across a 375px phone
             leaves each one 163px, and a 40px icon with its gap takes a third
             of what is left after the padding — which is why the label and the
             subtitle were both wrapping to nothing however they were clamped.
             The icon is decoration; the number is the point, and on a phone
             the number gets the room. */
          <span
            className={cn(
              'hidden sm:grid shrink-0 w-9 h-9 rounded-card place-items-center',
              toneClasses[tone],
            )}
          >
            {icon}
          </span>
        )}
      </div>
    </button>
  );
}

/* -----------------------------------------------------------------------------
 * A row of figures
 * -----------------------------------------------------------------------------
 * Three or four small numbers across the top of a card: taken / repaid /
 * outstanding, earned / paid / owed, days worked / absent / on leave. It was
 * written by hand in six places and every one of them made the same mistake —
 * `grid-cols-3`, with no breakpoint.
 *
 * That is fine until the number is money. Inside a card on a 360px phone the
 * padding takes 40px and two gaps take 24, which leaves 98px a column; "AED
 * 20,100.00" set at 17px bold is a shade over 100. So the figure either wrapped
 * mid-amount or pushed the card out of the screen — the same bug StatTile
 * already carries a comment about, arrived at independently six more times.
 *
 * The fix is not a breakpoint. A breakpoint answers "how wide is the screen",
 * and the question here is "how wide is the card", which is not the same thing
 * on a page that has a sidebar on Tuesday and not on Wednesday. `auto-fit` with
 * a floor asks the real question: fit as many columns as will hold a number,
 * and wrap the rest. Two across on a phone, three or four on a desk, and the
 * component never has to be told which.
 * -------------------------------------------------------------------------- */

export function FigureGrid({
  children, min = '9rem', className,
}: {
  children: ReactNode;
  /**
   * The narrowest a column may get before one is dropped to the next row.
   * 9rem holds a five-figure AED amount at the size Figure sets. Raise it for
   * longer values; lower it for counts, which are two or three characters.
   */
  min?: string;
  className?: string;
}) {
  return (
    <div
      className={cn('grid gap-3', className)}
      /* Inline because the value is genuinely a parameter. Tailwind builds its
         classes by scanning source text, so a class assembled at runtime is a
         class that was never generated — the rule would simply not exist. */
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}, 1fr))` }}
    >
      {children}
    </div>
  );
}

export function Figure({
  label, value, icon, tone, highlight, plain, align = 'start', className,
}: {
  label: ReactNode;
  value: ReactNode;
  /** Sits beside the label, never beside the value. */
  icon?: ReactNode;
  /** A text colour for the value — `text-ok`, `text-danger`. Not a background. */
  tone?: string;
  /** The one figure that is the answer, rather than the working. */
  highlight?: boolean;
  /**
   * No card behind it. For a row of figures that already sits inside something
   * — under a divider, inside a panel — where a second surface would read as a
   * box inside a box.
   */
  plain?: boolean;
  align?: 'start' | 'center';
  className?: string;
}) {
  return (
    <div
      className={cn(
        /* min-w-0 because a grid item will not shrink below its own content
           without it, which is how a long amount takes the whole row sideways
           instead of wrapping inside its own cell. */
        'min-w-0',
        plain ? 'py-0.5' : 'rounded-card p-3',
        !plain && (highlight ? 'bg-brand-subtle' : 'bg-surface-2'),
        align === 'center' && 'text-center',
        className,
      )}
    >
      <p
        className={cn(
          'text-micro uppercase tracking-caps text-subtle font-semibold',
          'leading-tight flex items-center gap-1',
          align === 'center' && 'justify-center',
        )}
      >
        {icon}
        <span className="truncate">{label}</span>
      </p>
      {/* A step smaller on a phone, for the same reason StatTile drops from 26
          to 21: a figure that fits is worth more than a figure that is large. */}
      <p
        className={cn(
          'text-[15px] sm:text-lead font-semibold mt-1 tabular wrap-anywhere',
          'leading-tight',
          tone,
        )}
      >
        {value}
      </p>
    </div>
  );
}

/* -----------------------------------------------------------------------------
 * Empty and loading states
 * -------------------------------------------------------------------------- */
export function EmptyState({
  icon, title, body, action,
}: { icon?: ReactNode; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="text-center py-14 px-6">
      {icon && <div className="text-subtle mb-3 flex justify-center">{icon}</div>}
      <h3 className="text-[15px] font-semibold">{title}</h3>
      {body && <p className="text-[13px] text-muted mt-1 max-w-sm mx-auto">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton rounded-card', className)} aria-hidden="true" />;
}

/* -----------------------------------------------------------------------------
 * Skeletons shaped like the thing that replaces them
 * -----------------------------------------------------------------------------
 * A `<Skeleton className="h-64" />` standing in for a whole screen is one grey
 * slab that shares nothing with what arrives: not the heading, not the cards,
 * not the number of rows. So the page appears to load twice — once as a
 * rectangle and once as itself — and everything jumps.
 *
 * These are the shapes this application actually uses. The point is not that
 * grey boxes are prettier in threes; it is that the layout should already be on
 * screen and settled before the data lands, so nothing moves when it does.
 *
 * A screen that renders its own headings and card frames while loading, and
 * uses one of these only where the data goes, does not jump at all.
 * -------------------------------------------------------------------------- */

/**
 * Lines of text, the last one short.
 *
 * Prose does not reach the right margin on its final line, and a block of equal
 * bars reads as a table rather than as a paragraph.
 */
export function SkeletonText({
  lines = 3, className,
}: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)} aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={cn('h-3.5 rounded-full', index === lines - 1 ? 'w-2/5' : 'w-full')}
        />
      ))}
    </div>
  );
}

/**
 * A list of rows, each with a mark, a name and a figure.
 *
 * The shape of nearly every list here — a person and a number, a site and a
 * total — so it stands in for most of them without being told which.
 */
export function SkeletonRows({
  rows = 4, className,
}: { rows?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)} aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-center gap-3 p-3 rounded-card bg-surface-2">
          <Skeleton className="w-9 h-9 rounded-full shrink-0" />
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <Skeleton className="h-3.5 w-1/3 rounded-full" />
            <Skeleton className="h-3 w-1/2 rounded-full" />
          </div>
          <Skeleton className="h-4 w-14 rounded-full shrink-0" />
        </div>
      ))}
    </div>
  );
}

/**
 * The figures across the top of a screen.
 *
 * Same grid as the real one, so the cards do not resize when the numbers
 * arrive — `min-w-0` included, because a grid item will not shrink below its
 * own content without it and that is what took the payroll screen sideways off
 * a 360px phone.
 */
export function SkeletonStats({
  count = 4, className,
}: { count?: number; className?: string }) {
  return (
    <div className={cn('grid gap-4 grid-cols-2 lg:grid-cols-4', className)} aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="bg-surface border border-line rounded-card p-4 min-w-0">
          <Skeleton className="h-2.5 w-2/3 rounded-full" />
          <Skeleton className="h-6 w-1/2 rounded-full mt-2.5" />
        </div>
      ))}
    </div>
  );
}

/**
 * The row of figures a FigureGrid draws, at the same widths.
 *
 * Uses the same auto-fit floor, so the placeholder wraps to two columns on a
 * phone exactly where the real figures do and nothing reflows when they land.
 */
export function SkeletonFigures({
  count = 3, min = '9rem', className,
}: { count?: number; min?: string; className?: string }) {
  return (
    <div
      className={cn('grid gap-3', className)}
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}, 1fr))` }}
      aria-hidden="true"
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="rounded-card p-3 bg-surface-2 min-w-0">
          <Skeleton className="h-2.5 w-3/5 rounded-full" />
          <Skeleton className="h-5 w-4/5 rounded-full mt-2" />
        </div>
      ))}
    </div>
  );
}

/**
 * What nearly every dialog in this application is: a few figures across the
 * top, then a list of what makes them up.
 *
 * The dialogs used to show one `h-96` slab. A dialog opens instantly, so that
 * slab is the first thing anybody sees, and it shares nothing with what
 * replaces it — which is why opening a payslip looked like a fault before it
 * looked like a payslip.
 */
export function SkeletonPanel({
  figures = 3, rows = 4, min, className,
}: { figures?: number; rows?: number; min?: string; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-5', className)}>
      {figures > 0 && <SkeletonFigures count={figures} min={min} />}
      {rows > 0 && <SkeletonRows rows={rows} />}
    </div>
  );
}

/**
 * Label-and-field pairs, in the two-column grid the forms here use.
 *
 * A settings screen replaced by a slab loses its own shape; this keeps the
 * columns, so the labels land where the grey was.
 */
export function SkeletonForm({
  fields = 6, className,
}: { fields?: number; className?: string }) {
  return (
    <div className={cn('grid gap-x-4 gap-y-5 sm:grid-cols-2', className)} aria-hidden="true">
      {Array.from({ length: fields }, (_, index) => (
        <div key={index} className="flex flex-col gap-2 min-w-0">
          <Skeleton className="h-3 w-24 rounded-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      ))}
    </div>
  );
}

/* -----------------------------------------------------------------------------
 * Money and duration formatting
 * -------------------------------------------------------------------------- */
export function formatMoney(amount: number, currency = 'AED', decimals = 2): string {
  /* The unit follows the reader; the digits do not.
     -------------------------------------------------------------------------
     'ar-AE' writes the currency as د.إ and puts it where Arabic puts it, which
     is what the office expects to see on screen. What it must NOT do is switch
     to Arabic-Indic numerals: a payroll column read against a bank statement
     has to carry the same digits the bank printed. `latn` pins that, and it is
     the same decision the exported documents already made.

     The language is read rather than passed. This is called from components,
     from table cells and from a few plain helpers that are not components at
     all — a parameter on each would be forty chances to forget one. */
  const arabic = currentLanguage() === 'ar';

  const format = new Intl.NumberFormat(arabic ? 'ar-AE-u-nu-latn' : 'en-AE', {
    style: 'currency',
    currency,
    /* (AED 1,234.50), never -AED 1,234.50. The house rule, and Intl's own
       accounting form rather than brackets glued on afterwards — Arabic puts
       the unit on the other side of the figure, and string surgery would put
       the bracket in the wrong place there. See src/core/money.ts. */
    currencySign: 'accounting',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  if (!isDirham(currency)) return format.format(amount);

  /* The dirham's own sign, in place of its letters.
     -------------------------------------------------------------------------
     Swapped through formatToParts rather than by replacing 'AED' in the
     finished string, because the locale decides where the unit goes and what
     sits beside it: English puts it in front, Arabic puts it after the figure,
     and the accounting brackets wrap both. Replacing text would work in one of
     those and quietly put the sign inside the bracket in the other.

     Only the dirham. A company set to rupees or dollars keeps its own sign —
     see src/core/dirham.ts. */
  return format.formatToParts(amount)
    .map((part) => (part.type === 'currency' ? DIRHAM_SIGN : part.value))
    .join('');
}

/**
 * An amount, coloured when it is below zero.
 *
 * formatMoney gives every negative its brackets, which is the half of the rule
 * that survives a photocopier. This is the other half, and it needs an element
 * to live on — a string cannot carry a colour, which is the whole reason this
 * exists rather than being folded into the formatter.
 *
 * Used wherever an amount is rendered as text. Where a plain string is needed —
 * a title attribute, a sentence, an export — formatMoney is still the answer,
 * and the brackets are already in it.
 */
export function Money({
  amount, currency = 'AED', decimals = 2, className,
}: {
  amount: number;
  currency?: string;
  decimals?: number;
  className?: string;
}) {
  /* The string, not <Money> again.
     -------------------------------------------------------------------------
     This line read `<Money amount={amount} … />` — the component rendering
     itself, with nothing to stop it. It got here from the rewrite that turned
     `formatMoney(...)` into `<Money … />` across the app, which rewrote the one
     call site it should have left alone: the inside of Money.

     In a browser that does not throw. React walks the tree in a loop rather
     than by recursion, so it simply kept building another Money inside the last
     one until the tab ran out of memory — thirty or forty seconds of a frozen
     screen and then "Aw, Snap! Out of Memory", on every page that showed an
     amount. The employee page, which shows three panels of them, froze on
     arrival every time. See components/aComponentNeverRendersItself.test.ts. */
  return (
    <span className={cn(amount < 0 && 'text-danger', className)}>
      {formatMoney(amount, currency, decimals)}
    </span>
  );
}

export function formatHours(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);

  /* س for ساعة and د for دقيقة, the same abbreviation the exported documents
     use. Spelling them out is correct and three times the width, which in a
     table column costs a column somebody needed. */
  if (currentLanguage() === 'ar') {
    return m === 0 ? `${h}س` : `${h}س ${m}د`;
  }
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
